import { requestWebsite } from '@va/dashboard/actions/api';
import { useResetData } from '@va/dashboard/api-client/Settings';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { ModalFormWrapper } from '@va/ui/components/modals';
import { CheckboxInput, Paragraph, TextInput } from '@va/ui/design-system';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { object, string } from 'yup';

const RESET = 'RESET';

enum FormFieldsNames {
  sendDataSelected = 'sendDataSelected',
  password = 'password',
}

export const ResetDataModalWix = ({ isModalOpen, onClose }: { isModalOpen: boolean; onClose: () => void }) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const resetDataApi = useResetData();

  const { values, errors, handleChange, setFieldValue, touched, handleSubmit, resetForm } = useFormik({
    initialValues: {
      [FormFieldsNames.sendDataSelected]: false,
      [FormFieldsNames.password]: '',
    },
    validationSchema: object().shape({
      [FormFieldsNames.password]: string()
        .required(translate('form.errors.requiredField'))
        .matches(/^RESET$/, translate('form.errors.invalidResetString')),
    }),
    onSubmit: (values) => {
      resetDataApi.execute(values.sendDataSelected);
    },
  });

  useEffect(() => {
    if (resetDataApi.isSucceeded) {
      showSuccessNotification(2000, translate('all.generalSettings.resetData.success'));
      dispatch(requestWebsite());
      onClose();
    }
    if (resetDataApi.error) {
      showErrorNotification();
    }
  }, [
    resetDataApi.isSucceeded,
    resetDataApi.error,
    showErrorNotification,
    showSuccessNotification,
    onClose,
    dispatch,
    translate,
  ]);

  return (
    <ModalFormWrapper
      title={translate('all.generalSettings.resetData.title')}
      isOpen={isModalOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      onSubmit={handleSubmit}
      saveButtonProps={{
        text: resetDataApi.isLoading ? translate('button.isLoading') : translate('button.resetData'),
        color: 'tertiary',
      }}
    >
      <div className='flex flex-col p-3 gap-3'>
        <Paragraph>{translate('all.generalSettings.resetData.text1')}</Paragraph>
        <CheckboxInput
          label={translate('form.labels.sendDataToEmail')}
          name={FormFieldsNames.sendDataSelected}
          id='termsAndDpaAccepted'
          checked={values.sendDataSelected}
          onChange={handleChange}
          labelClassName='text-15'
        />
        <Paragraph>
          {translate('wix.generalSettings.resetData.confirmationText', {
            reset: RESET,
          })}
        </Paragraph>
        <TextInput
          label={translate('form.labels.resetConfirmationText', {
            reset: RESET,
          })}
          placeholder={translate('form.placeholders.resetConfirmationText', {
            reset: RESET,
          })}
          name={FormFieldsNames.password}
          required
          onChange={handleChange}
          clearField={() => {
            setFieldValue(FormFieldsNames.password, '');
          }}
          value={values.password}
          autoComplete='new-password'
          error={errors.password && touched.password ? translate(errors.password) : undefined}
          autoFocus
        />
      </div>
    </ModalFormWrapper>
  );
};
